/**
 * Numbering Series Master — admin page for code/number generation rules.
 * ─────────────────────────────────────────────────────────────────────────────
 * Single source of truth for every code generated by the ERP. Configure
 * prefix, role code, separator, year inclusion, padding, etc. per
 * (scope, document_type) tuple. Live preview shows the format as you type.
 *
 * Scope chain: plant → company → project default. The admin can edit any
 * level. The service layer auto-walks the chain at code-generation time.
 */
import { useMemo, useState } from 'react'
import {
  Alert, App, Button, Card, Col, Drawer, Empty, Form, Input,
  InputNumber, Popconfirm, Row, Select, Space, Switch, Table,
  Tag, Tooltip, Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  EditOutlined, EyeOutlined, NumberOutlined, PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/shared/api/client'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

interface NumberingSeries {
  id: string
  company_id: string | null
  plant_id: string | null
  document_type: string
  prefix: string | null
  role_code: string | null
  separator: string
  include_year: boolean
  year_format: string | null
  year_position: string
  number_pad_digits: number
  start_number: number
  suffix: string | null
  reset_policy: string
  is_active: boolean
  notes: string | null
  preview: string | null
}

const DOCUMENT_TYPES = [
  // Permanent identifiers
  { value: 'CUSTOMER',            label: 'Customer (Debtor)',  group: 'Permanent Identifiers' },
  { value: 'SUPPLIER',            label: 'Supplier (Creditor)', group: 'Permanent Identifiers' },
  { value: 'ITEM',                label: 'Item / SKU',          group: 'Permanent Identifiers' },
  // Transactional
  { value: 'MILL_LOT',            label: 'Mill Lot',            group: 'Transactional' },
  { value: 'GRN',                 label: 'GRN',                 group: 'Transactional' },
  { value: 'BALE',                label: 'Bale',                group: 'Transactional' },
  { value: 'SALES_ORDER',         label: 'Sales Order',         group: 'Transactional' },
  { value: 'DISPATCH',            label: 'Dispatch',            group: 'Transactional' },
  { value: 'INVOICE',             label: 'Invoice',             group: 'Transactional' },
  { value: 'PURCHASE_ORDER',      label: 'Purchase Order',      group: 'Transactional' },
  { value: 'MIXING_PLAN',         label: 'Mixing Plan',         group: 'Transactional' },
  { value: 'PRODUCTION_CAMPAIGN', label: 'Production Campaign', group: 'Transactional' },
  { value: 'STOCK_BUILD_AUTH',    label: 'Stock-Build Auth',    group: 'Transactional' },
  { value: 'YARN_TEST',           label: 'Yarn Test',           group: 'Transactional' },
  { value: 'LAYDOWN',             label: 'Laydown',             group: 'Transactional' },
  { value: 'ISSUE_SLIP',          label: 'Issue Slip',          group: 'Transactional' },
]

export default function NumberingSeriesPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<NumberingSeries | null>(null)
  const [form] = Form.useForm()

  // ─── Data ────────────────────────────────────────────────────────────
  const { data: series = [], isLoading, refetch } = useQuery<NumberingSeries[]>({
    queryKey: ['numbering-series'],
    queryFn: () =>
      apiClient.get('/numbering-series', { params: { include_inactive: true } })
        .then(r => r.data),
  })

  // ─── Live preview while editing ──────────────────────────────────────
  const [livePreview, setLivePreview] = useState<{
    sample_first: string; sample_tenth: string; sample_last: string
  } | null>(null)

  const previewMut = useMutation({
    mutationFn: (body: any) =>
      apiClient.post('/numbering-series/preview', body).then(r => r.data),
    onSuccess: (data) => setLivePreview(data),
    onError: () => setLivePreview(null),
  })

  const refreshPreview = () => {
    const v = form.getFieldsValue()
    if (!v.prefix && !v.role_code) {
      setLivePreview(null)
      return
    }
    previewMut.mutate({
      prefix: v.prefix || null,
      role_code: v.role_code || null,
      separator: v.separator || '-',
      include_year: !!v.include_year,
      year_format: v.year_format || null,
      year_position: v.year_position || 'AFTER_PREFIX',
      number_pad_digits: v.number_pad_digits ?? 4,
      start_number: v.start_number ?? 1,
      suffix: v.suffix || null,
    })
  }

  // ─── Save ─────────────────────────────────────────────────────────────
  const upsertMut = useMutation({
    mutationFn: (body: any) =>
      apiClient.post('/numbering-series', body).then(r => r.data),
    onSuccess: () => {
      message.success('Series saved ✓')
      qc.invalidateQueries({ queryKey: ['numbering-series'] })
      setDrawerOpen(false); setEditing(null); form.resetFields()
      setLivePreview(null)
    },
    onError: (e: any) =>
      message.error(e.response?.data?.detail || 'Save failed'),
  })

  const handleSubmit = () => {
    form.validateFields().then(vals => {
      upsertMut.mutate({
        document_type: vals.document_type,
        company_id: vals.company_id || null,
        plant_id: vals.plant_id || null,
        prefix: vals.prefix || null,
        role_code: vals.role_code || null,
        separator: vals.separator || '-',
        include_year: !!vals.include_year,
        year_format: vals.year_format || null,
        year_position: vals.year_position || 'AFTER_PREFIX',
        number_pad_digits: vals.number_pad_digits ?? 4,
        start_number: vals.start_number ?? 1,
        suffix: vals.suffix || null,
        reset_policy: vals.reset_policy || 'NEVER',
        notes: vals.notes || null,
        is_active: vals.is_active !== false,
      })
    })
  }

  const openEdit = (s: NumberingSeries) => {
    setEditing(s)
    form.setFieldsValue(s)
    setLivePreview({
      sample_first: s.preview ?? '',
      sample_tenth: '',
      sample_last: '',
    })
    setDrawerOpen(true)
  }

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({
      separator: '-', include_year: false, year_position: 'AFTER_PREFIX',
      number_pad_digits: 4, start_number: 1, reset_policy: 'NEVER',
      is_active: true,
    })
    setLivePreview(null)
    setDrawerOpen(true)
  }

  // ─── KPIs ─────────────────────────────────────────────────────────────
  const summary = useMemo(() => {
    const active = series.filter(s => s.is_active).length
    const projectDefaults = series.filter(s => !s.company_id && !s.plant_id).length
    const companyOverrides = series.filter(s => s.company_id !== null).length
    const plantOverrides = series.filter(s => s.plant_id !== null).length
    return { active, projectDefaults, companyOverrides, plantOverrides }
  }, [series])

  // ─── Columns ─────────────────────────────────────────────────────────
  const columns: ColumnsType<NumberingSeries> = [
    {
      title: 'Document Type', dataIndex: 'document_type', key: 'dt', width: 200,
      render: (v: string) => {
        const meta = DOCUMENT_TYPES.find(d => d.value === v)
        return (
          <Space direction="vertical" size={0}>
            <Text strong style={{ fontSize: 12 }}>{meta?.label ?? v}</Text>
            <Text type="secondary" style={{ fontSize: 10 }}>{v}</Text>
          </Space>
        )
      },
    },
    {
      title: 'Scope', key: 'scope', width: 130,
      render: (_: unknown, r) => {
        if (r.company_id) return <Tag color="blue">Company</Tag>
        if (r.plant_id)   return <Tag color="purple">Plant</Tag>
        return <Tag>Project Default</Tag>
      },
    },
    {
      title: 'Preview', dataIndex: 'preview', key: 'pv', width: 200,
      render: (v: string | null) => (
        v ? <Text code style={{ fontSize: 13, color: '#1677ff' }}>{v}</Text>
          : <Text type="secondary">—</Text>
      ),
    },
    {
      title: 'Format', key: 'fmt', width: 200,
      render: (_: unknown, r) => (
        <Space direction="vertical" size={0} style={{ fontSize: 11 }}>
          <Text>
            {[r.prefix, r.role_code, r.include_year ? r.year_format : null,
              `[${r.number_pad_digits}d]`, r.suffix].filter(Boolean).join(r.separator)}
          </Text>
          <Text type="secondary" style={{ fontSize: 10 }}>
            start: {r.start_number}, reset: {r.reset_policy.replace(/_/g, ' ')}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Active', dataIndex: 'is_active', key: 'ac', width: 80,
      render: (v: boolean) => v
        ? <Tag color="green">Active</Tag>
        : <Tag color="default">Off</Tag>,
    },
    {
      title: 'Action', key: 'act', width: 100,
      render: (_: unknown, r) => (
        <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)}>
          Edit
        </Button>
      ),
    },
  ]

  return (
    <App>
      <PageHeader
        title="Numbering Series"
        subtitle="Universal master controlling every code/number format in the ERP — KPMG / SAP style"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>Refresh</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Series
            </Button>
          </Space>
        }
      />

      {/* KPI strip */}
      <Row gutter={12} style={{ marginBottom: 16 }}>
        <Col xs={6}>
          <Card size="small">
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 11 }}>Active</Text>
              <Text strong style={{ fontSize: 22 }}>{summary.active}</Text>
            </Space>
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small">
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 11 }}>Project Defaults</Text>
              <Text strong style={{ fontSize: 22 }}>{summary.projectDefaults}</Text>
            </Space>
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small">
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 11 }}>Company Overrides</Text>
              <Text strong style={{ fontSize: 22 }}>{summary.companyOverrides}</Text>
            </Space>
          </Card>
        </Col>
        <Col xs={6}>
          <Card size="small">
            <Space direction="vertical" size={0}>
              <Text type="secondary" style={{ fontSize: 11 }}>Plant Overrides</Text>
              <Text strong style={{ fontSize: 22 }}>{summary.plantOverrides}</Text>
            </Space>
          </Card>
        </Col>
      </Row>

      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Scope chain: Plant → Company → Project Default"
        description="When code is generated, the system walks this chain to find the most specific match. Edit project defaults to change all plants at once; add a company-scoped or plant-scoped row to override for that scope only."
      />

      <Card size="small">
        <Table
          rowKey="id" size="small" loading={isLoading}
          dataSource={series} columns={columns}
          scroll={{ x: 1000 }}
          pagination={{ pageSize: 25, showTotal: t => `${t} series` }}
        />
      </Card>

      {/* Edit drawer with live preview */}
      <Drawer
        title={
          <Space>
            <NumberOutlined style={{ color: '#1677ff' }} />
            {editing ? `Edit Series — ${editing.document_type}` : 'New Numbering Series'}
          </Space>
        }
        open={drawerOpen}
        width={640}
        onClose={() => { setDrawerOpen(false); setEditing(null); form.resetFields() }}
        footer={
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setDrawerOpen(false)}>Cancel</Button>
            <Button type="primary" onClick={handleSubmit}
              loading={upsertMut.isPending}>
              {editing ? 'Save Changes' : 'Create Series'}
            </Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical" onValuesChange={refreshPreview}>
          <Form.Item name="document_type" label="Document Type"
            rules={[{ required: true }]}>
            <Select
              showSearch optionFilterProp="label"
              disabled={!!editing}
              options={DOCUMENT_TYPES.map(d => ({
                value: d.value, label: `${d.label}  (${d.group})`,
              }))}
            />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="prefix" label="Prefix"
                tooltip="e.g. UCL, GRN, MX. Leave blank for no prefix.">
                <Input placeholder="UCL" maxLength={20} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="role_code" label="Role Code"
                tooltip="DR for debtor, CR for creditor, ITM for items, blank for none.">
                <Input placeholder="DR / CR / ITM" maxLength={10} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="separator" label="Separator">
                <Select options={[
                  { value: '-', label: '-  (hyphen)' },
                  { value: '/', label: '/  (slash)' },
                  { value: '_', label: '_  (underscore)' },
                  { value: '.', label: '.  (dot)' },
                  { value: '',  label: '(none)' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="number_pad_digits" label="Number Padding"
                tooltip="4 → 0001, 5 → 00001 (use 5 for high-volume entities like Bales)">
                <InputNumber style={{ width: '100%' }} min={1} max={10} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="start_number" label="Start Number"
                tooltip="Most start at 1; some companies start at 1001 to match an old paper register.">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item name="suffix" label="Suffix"
                tooltip="Optional trailing tag (rarely used)">
                <Input placeholder="X" maxLength={20} />
              </Form.Item>
            </Col>
          </Row>

          {/* Year toggle + format */}
          <Row gutter={12}>
            <Col span={6}>
              <Form.Item name="include_year" label="Include Year" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => (
                  <Form.Item name="year_format" label="Year Format"
                    tooltip="YYYY: 2026, YY: 26, FY: 2026-27 (Indian financial year)">
                    <Select disabled={!getFieldValue('include_year')}
                      options={[
                        { value: 'YYYY', label: 'YYYY  (e.g. 2026)' },
                        { value: 'YY',   label: 'YY    (e.g. 26)' },
                        { value: 'FY',   label: 'FY    (Indian: 2026-27)' },
                      ]} />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
            <Col span={9}>
              <Form.Item shouldUpdate noStyle>
                {({ getFieldValue }) => (
                  <Form.Item name="year_position" label="Year Position">
                    <Select disabled={!getFieldValue('include_year')}
                      options={[
                        { value: 'AFTER_PREFIX', label: 'After Prefix  (PFX-YEAR-...)' },
                        { value: 'AFTER_ROLE',   label: 'After Role    (PFX-ROLE-YEAR-...)' },
                      ]} />
                  </Form.Item>
                )}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="reset_policy" label="Counter Reset Policy"
                tooltip="NEVER: counter just keeps growing. YEARLY: resets each calendar year. FY_INDIA: resets each Indian financial year on April 1.">
                <Select options={[
                  { value: 'NEVER', label: 'Never (continuous)' },
                  { value: 'YEARLY', label: 'Yearly (Jan 1)' },
                  { value: 'FY_INDIA', label: 'Indian FY (April 1)' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          {/* Live preview */}
          {livePreview && (
            <Card size="small" style={{ background: '#f0f8ff', borderColor: '#1677ff' }}>
              <Space direction="vertical" size={4}>
                <Text style={{ fontSize: 11 }} type="secondary">
                  <EyeOutlined /> Live Preview
                </Text>
                <Space wrap>
                  <Tag color="blue" style={{ fontSize: 13, padding: '4px 10px' }}>
                    First: <Text code>{livePreview.sample_first}</Text>
                  </Tag>
                  <Tag style={{ fontSize: 13, padding: '4px 10px' }}>
                    10th: <Text code>{livePreview.sample_tenth}</Text>
                  </Tag>
                  <Tooltip title="What the LAST possible number looks like at this padding">
                    <Tag style={{ fontSize: 13, padding: '4px 10px' }}>
                      Max: <Text code>{livePreview.sample_last}</Text>
                    </Tag>
                  </Tooltip>
                </Space>
              </Space>
            </Card>
          )}

          <Form.Item name="notes" label="Notes" style={{ marginTop: 12 }}>
            <Input.TextArea rows={2}
              placeholder="Any context for this configuration..." />
          </Form.Item>
        </Form>
      </Drawer>
    </App>
  )
}
